import { FC, useState, useEffect } from "react";
import { Squares2X2Icon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import ImageCarouselModal from "./ImageCarouselModal";

interface ImageMosaicGalleryProps {
  coverImage: string;
  galleryImages: string[];
  onOpenGallery: () => void;
  alt?: string;
}

const ImageMosaicGallery: FC<ImageMosaicGalleryProps> = ({
  coverImage,
  galleryImages = [],
  onOpenGallery,
  alt = "Gallery"
}) => {
  const { t } = useTranslation("translation");
  
  // State to track the current main image and modified gallery
  const [currentMainImage, setCurrentMainImage] = useState(coverImage);
  const [currentMainIndex, setCurrentMainIndex] = useState(0);
  const [modifiedGalleryImages, setModifiedGalleryImages] = useState([...galleryImages]);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);

  // Create combined images array for modal (cover + all gallery images)
  const allImages = [currentMainImage, ...modifiedGalleryImages.filter(img => img)];

  // Filter gallery images to show max 3 images (indices 0-3 for first 3 images)
  const displayedImages = modifiedGalleryImages.filter((_, i) => i < 3);

  // Update state when props change
  useEffect(() => {
    setCurrentMainImage(coverImage);
    setModifiedGalleryImages([...galleryImages]);
    setCurrentMainIndex(0);
  }, [coverImage, galleryImages]);

  // Determine if we have additional images beyond the cover image
  const hasAdditionalImages = displayedImages.length > 0;

  // Handle image swap when thumbnail is clicked
  const handleThumbnailClick = (image: string, index: number) => {
    const actualIndex = index + 1; // +1 because displayedImages start from index 1 of galleryImages
    
    // Create new gallery array with swapped images
    const newGalleryImages = [...modifiedGalleryImages];
    
    // Swap the main image with the clicked thumbnail
    const temp = currentMainImage;
    newGalleryImages[actualIndex] = temp;
    
    // Update states
    setCurrentMainImage(image);
    setCurrentMainIndex(actualIndex);
    setModifiedGalleryImages(newGalleryImages);
  };

  // Modal handlers
  const handleOpenModal = (imageIndex: number = 0) => {
    setModalInitialIndex(imageIndex);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };


  return (
    <div className="rounded-md sm:rounded-xl px-2">
      <div className={`relative grid gap-1 sm:gap-2 h-[80vh] w-full rounded-md sm:rounded-xl overflow-hidden ${hasAdditionalImages ? 'grid-cols-4 grid-rows-3' : 'grid-cols-1'
        }`}>
        {/* Main cover image */}
        <div
          className={`relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer ${hasAdditionalImages ? 'col-span-3 row-span-3' : 'col-span-1'
            }`}
          onClick={onOpenGallery}
        >
          <img
            alt={`${alt} ${currentMainIndex + 1}`}
            className="object-cover rounded-md sm:rounded-xl w-full h-full"
            src={currentMainImage || ""}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
          />
          <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
        </div>

        {/* Gallery thumbnails - only show if we have additional images */}
        {hasAdditionalImages && displayedImages.map((item, index) => (
          <div
            key={index}
            className="relative rounded-md sm:rounded-xl overflow-hidden row-span-1"
          >
            <img
              alt={`${alt} ${index + 2}`}
              className="object-cover rounded-md sm:rounded-xl w-full h-full"
              src={item || ""}
              sizes="400px"
            />

            {/* Overlay */}
            <div
              className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
              onClick={() => handleThumbnailClick(item, index)}
            />
          </div>
        ))}

        {/* Show gallery button */}
        <div
          className="absolute hidden md:flex md:items-center md:justify-center left-3 bottom-3 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-500 cursor-pointer hover:bg-neutral-200 z-10"
          onClick={() => handleOpenModal(0)}
        >
          <Squares2X2Icon className="h-5 w-5" />
          <span className="ml-2 text-neutral-800 text-sm font-medium">
            {t("detailPages.common.showGallery")}
          </span>
        </div>
      </div>
      
      {/* Full-screen carousel modal */}
      <ImageCarouselModal
        images={allImages}
        isOpen={isModalOpen}
        initialIndex={modalInitialIndex}
        onClose={handleCloseModal}
        alt={alt}
      />
    </div>
  );
};

export default ImageMosaicGallery;
